import { useState, useEffect, useCallback } from 'react';
import { messageService, Conversation, Message, SendMessageData } from '../services/messageService';
import { useApp } from '../context/AppContext';

const emitDebugLog = (payload: {
  runId: string;
  hypothesisId: string;
  location: string;
  message: string;
  data: Record<string, unknown>;
}) => {
  const eventPayload = {
    sessionId: '654aec',
    runId: payload.runId,
    hypothesisId: payload.hypothesisId,
    location: payload.location,
    message: payload.message,
    data: payload.data,
    timestamp: Date.now(),
  };

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([JSON.stringify(eventPayload)], { type: 'application/json' });
    navigator.sendBeacon('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d', blob);
  }

  fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': '654aec',
    },
    body: JSON.stringify(eventPayload),
  }).catch(() => {});
};

export const useMessages = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const { state } = useApp();
  const user = state.user;

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { conversations: convos } = await messageService.getConversations();
      setConversations(convos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await messageService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  const sendMessage = useCallback(async (data: SendMessageData) => {
    try {
      const result = await messageService.sendMessage(data);
      await fetchConversations(); // Refresh conversations
      return result;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to send message');
    }
  }, [fetchConversations]);

  const markAsRead = useCallback(async (conversationId: string) => {
    try {
      await messageService.markAsRead(conversationId);
      await Promise.all([fetchConversations(), fetchUnreadCount()]);
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
    }
  }, [fetchConversations, fetchUnreadCount]);

  // Subscribe to real-time unread count updates
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = messageService.subscribeToUnreadCount(user.id, (count) => {
      setUnreadCount(count);
    });

    return () => unsubscribe();
  }, [user?.id]);

  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
  }, [fetchConversations, fetchUnreadCount]);

  return {
    conversations,
    loading,
    error,
    unreadCount,
    sendMessage,
    markAsRead,
    refresh: fetchConversations,
    refreshUnreadCount: fetchUnreadCount,
  };
};

export const useConversation = (conversationId: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;
    
    try {
      setLoading(true);
      // #region agent log
      emitDebugLog({
        runId: 'initial',
        hypothesisId: 'H2',
        location: 'web/src/hooks/useMessages.ts:fetchMessages:start',
        message: 'fetchMessages started',
        data: { conversationId },
      });
      // #endregion
      setError(null);
      const { messages: msgs } = await messageService.getMessages(conversationId);
      setMessages(msgs);
      // #region agent log
      emitDebugLog({
        runId: 'initial',
        hypothesisId: 'H2',
        location: 'web/src/hooks/useMessages.ts:fetchMessages:success',
        message: 'fetchMessages resolved',
        data: { conversationId, messageCount: msgs.length },
      });
      // #endregion
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  // Subscribe to real-time message updates
  useEffect(() => {
    if (!conversationId) return;

    // Initial fetch
    fetchMessages();
    // #region agent log
    emitDebugLog({
      runId: 'initial',
      hypothesisId: 'H3',
      location: 'web/src/hooks/useMessages.ts:useEffect:subscribe',
      message: 'Subscribing to conversation messages',
      data: { conversationId },
    });
    // #endregion

    // Subscribe to real-time updates
    const unsubscribe = messageService.subscribeToConversationMessages(
      conversationId,
      (realtimeMessages) => {
        // #region agent log
        emitDebugLog({
          runId: 'initial',
          hypothesisId: 'H3',
          location: 'web/src/hooks/useMessages.ts:subscribeCallback',
          message: 'Realtime snapshot received',
          data: { conversationId, messageCount: realtimeMessages.length },
        });
        // #endregion
        setMessages((previousMessages) => {
          // Keep initial API-loaded content if a transient empty snapshot arrives.
          if (realtimeMessages.length === 0 && previousMessages.length > 0) {
            return previousMessages;
          }

          return realtimeMessages;
        });
      }
    );

    return () => {
      // #region agent log
      emitDebugLog({
        runId: 'initial',
        hypothesisId: 'H1',
        location: 'web/src/hooks/useMessages.ts:useEffect:cleanup',
        message: 'Unsubscribing from conversation messages',
        data: { conversationId },
      });
      // #endregion
      unsubscribe();
    };
  }, [conversationId, fetchMessages]);

  return {
    messages,
    loading,
    error,
    refresh: fetchMessages,
  };
};

export const useConversationWithUser = (userId: string | null) => {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConversation = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      const { conversationId: convId, messages: msgs } = await messageService.getMessagesWithUser(userId);
      setConversationId(convId);
      setMessages(msgs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Subscribe to real-time message updates
  useEffect(() => {
    if (!conversationId) return;

    const unsubscribe = messageService.subscribeToConversationMessages(
      conversationId,
      (realtimeMessages) => {
        setMessages((previousMessages) => {
          if (realtimeMessages.length === 0 && previousMessages.length > 0) {
            return previousMessages;
          }

          return realtimeMessages;
        });
      }
    );

    return () => unsubscribe();
  }, [conversationId]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  return {
    conversationId,
    messages,
    loading,
    error,
    refresh: fetchConversation,
  };
};

